import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useWebSocket,
  WebSocketMessage,
  useSystemSetting,
} from '@fdha/common-hooks';
import { useSnackbar } from '@fdha/web-ui-library';
import { useListDeliveriesQuery, DeliveryV2 } from '@fdha/graphql-api-admin';
import { getFromDateForDeliveriesQuery } from '@fdha/common-utils';
import pRetry from 'p-retry';

/**
 * Custom hook that fetches patient deliveries and handles real-time synchronization
 * via WebSocket events.
 *
 * This hook:
 * 1. Fetches the offset value from system settings
 * 2. Queries deliveries for the specified patient
 * 3. Listens for WebSocket sync events to update deliveries in real-time
 * 4. Manages loading states for initial fetch and sync operations
 *
 * @param patientId - The ID of the patient whose deliveries to fetch
 *
 * @returns {Object} An object containing:
 *   - deliveries: Array of DeliveryV2 objects or undefined
 *   - isLoading: Boolean indicating if data is being loaded (initial or sync)
 */

export const useGetDeliveriesWithSync = (patientId: string) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const { addListener, removeListener } = useWebSocket();
  const { showSnackbar } = useSnackbar();
  const showSnackbarRef = useRef(showSnackbar);

  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  const { value: offsetValue, isLoading: isLoadingSystemSetting } =
    useSystemSetting<number>('offsetForDeliveriesQuery');

  const {
    data,
    loading: isQueryLoading,
    refetch,
  } = useListDeliveriesQuery({
    variables: {
      date: getFromDateForDeliveriesQuery(offsetValue || 0),
      patientId,
    },
    fetchPolicy: 'cache-and-network',
    skip: !patientId || isLoadingSystemSetting,
  });

  /**
   * WebSocket event handler that triggers a refetch when a sync event
   * for the current patient's deliveries is received.
   */
  const handleSync = useCallback(
    async (message: WebSocketMessage) => {
      if (!patientId || isLoadingSystemSetting) {
        return;
      }

      if (
        message.eventType !== 'sync' ||
        message.dataType !== 'syncDeliveries'
      ) {
        return;
      }

      if (message.data?.patientId !== patientId) {
        return;
      }

      setIsSyncing(true);
      const refetchDeliveries = async () => {
        const response = await refetch();
        if (response?.error) {
          throw new Error(response?.error?.message);
        }

        return response;
      };

      try {
        await pRetry(refetchDeliveries, {
          retries: 4,
        });
      } catch (error) {
        showSnackbarRef.current({
          message: 'Error fetching deliveries. Please refresh the page',
          severity: 'error',
        });
      } finally {
        setIsSyncing(false);
      }
    },
    [patientId, refetch, isLoadingSystemSetting]
  );

  useEffect(() => {
    addListener(handleSync);

    return () => {
      removeListener(handleSync);
    };
  }, [addListener, removeListener, handleSync]);

  return {
    deliveries: data?.deliveriesV2 as DeliveryV2[] | undefined,
    isLoading: isQueryLoading || isSyncing || isLoadingSystemSetting,
  };
};
